import React from 'react';
import type { CalendarContextSummary } from '../../types/messages';

type Props = {
  text: string;
  summary: CalendarContextSummary;
  onSelectDate?: (dateText: string) => void;
};

const weekdayFormatter = new Intl.DateTimeFormat('ja-JP', { weekday: 'short' });

const formatCalendarDate = (dateText: string) => {
  const [year, month, day] = dateText.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return `${month}/${day}(${weekdayFormatter.format(date)})`;
};

const formatPeriod = (from: string, to: string) => {
  return `${formatCalendarDate(from)} - ${formatCalendarDate(to)}`;
};

const formatDaySummary = (timedEventsCount: number, allDayBlockers: string[]) => {
  if (allDayBlockers.length > 0) {
    return '終日予定あり';
  }

  if (timedEventsCount === 0) {
    return '予定なし';
  }

  return `${timedEventsCount}件の予定`;
};

const formatMinutes = (minutes: number) => {
  if (minutes === 0) {
    return '0分';
  }

  const hours = Math.floor(minutes / 60);
  const remainMinutes = minutes % 60;

  if (hours === 0) {
    return `${remainMinutes}分`;
  }

  if (remainMinutes === 0) {
    return `${hours}時間`;
  }

  return `${hours}時間${remainMinutes}分`;
};

const renderDateChips = (dates: string[], onSelectDate?: (dateText: string) => void) => {
  return (
    <div style={styles.chipRow}>
      {dates.map(date => (
        <button
          key={date}
          type="button"
          style={styles.dateChipButton}
          onClick={() => onSelectDate?.(date)}
          disabled={onSelectDate === undefined}
        >
          <span style={styles.dateChip}>{formatCalendarDate(date)}</span>
        </button>
      ))}
    </div>
  );
};

const CalendarContextSummaryMessage = ({ text, summary, onSelectDate }: Props) => {
  const displayedDates = summary.suggestedDates.length > 0 ? summary.suggestedDates : summary.candidateDates;

  return (
    <div style={styles.card}>
      <div style={styles.lead}>{text}</div>
      <div style={styles.periodSection}>
        <div style={styles.periodLabel}>{summary.period.label}</div>
        <div style={styles.periodRange}>{formatPeriod(summary.period.from, summary.period.to)}</div>
      </div>
      {displayedDates.length > 0 && (
        <div style={styles.section}>
          <div style={styles.sectionTitle}>確認候補</div>
          {renderDateChips(displayedDates, onSelectDate)}
        </div>
      )}
      <div style={styles.section}>
        <div style={styles.sectionTitle}>日ごとの予定状況</div>
        <div style={styles.dayList}>
          {summary.days.map(day => (
            <button
              key={day.date}
              type="button"
              style={styles.dayCardButton}
              onClick={() => onSelectDate?.(day.date)}
              disabled={onSelectDate === undefined}
            >
              <div style={styles.dayCard}>
                <div style={styles.dayHeader}>
                  <div style={styles.dayTitle}>{formatCalendarDate(day.date)}</div>
                  <div style={styles.dayStatus}>{formatDaySummary(day.timedEventsCount, day.allDayBlockers)}</div>
                </div>
                <div style={styles.metricRow}>
                  <div style={styles.metric}>
                    <div style={styles.metricLabel}>予定件数</div>
                    <div style={styles.metricValue}>{day.timedEventsCount}件</div>
                  </div>
                  <div style={styles.metric}>
                    <div style={styles.metricLabel}>予定時間</div>
                    <div style={styles.metricValue}>{formatMinutes(day.totalBusyMinutes)}</div>
                  </div>
                  <div style={styles.metric}>
                    <div style={styles.metricLabel}>最大空き時間</div>
                    <div style={styles.metricValue}>{formatMinutes(day.longestFreeSlotMinutes)}</div>
                  </div>
                </div>
                {day.allDayBlockers.length > 0 && (
                  <div style={styles.detailBlock}>
                    <div style={styles.detailTitle}>終日予定</div>
                    <div style={styles.blockerList}>
                      {day.allDayBlockers.map(blocker => (
                        <span key={blocker} style={styles.blockerChip}>
                          {blocker}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {day.busySummaries.length > 0 && (
                  <div style={styles.detailBlock}>
                    <div style={styles.detailTitle}>予定詳細</div>
                    <ul style={styles.summaryList}>
                      {day.busySummaries.map(summaryText => (
                        <li key={summaryText} style={styles.summaryItem}>
                          {summaryText}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {day.allDayBlockers.length === 0 && day.busySummaries.length === 0 && (
                  <div style={styles.emptyState}>この日の予定は確認できませんでした。</div>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const styles = {
  card: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '16px',
    padding: '16px',
    borderRadius: '16px',
    border: '1px solid #d9e4ef',
    backgroundColor: '#f7fbff',
    boxShadow: '0 8px 24px rgba(20, 55, 90, 0.08)'
  },
  lead: {
    color: '#35526d',
    fontSize: '14px',
    lineHeight: 1.7
  },
  periodSection: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
    padding: '12px 14px',
    borderRadius: '12px',
    backgroundColor: '#ffffff',
    border: '1px solid #d8e4ee'
  },
  periodLabel: {
    color: '#11324d',
    fontSize: '14px',
    fontWeight: 700
  },
  periodRange: {
    color: '#4f6f89',
    fontSize: '13px'
  },
  section: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '10px'
  },
  sectionTitle: {
    color: '#35526d',
    fontSize: '13px',
    fontWeight: 700
  },
  chipRow: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '8px'
  },
  dateChipButton: {
    padding: 0,
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer'
  },
  dateChip: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '6px 10px',
    borderRadius: '999px',
    backgroundColor: '#e7f2fb',
    color: '#11324d',
    fontSize: '12px',
    fontWeight: 600
  },
  dayList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '10px'
  },
  dayCardButton: {
    padding: 0,
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    textAlign: 'left' as const
  },
  dayCard: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '12px',
    padding: '14px',
    borderRadius: '12px',
    backgroundColor: '#ffffff',
    border: '1px solid #d8e4ee'
  },
  dayHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: '12px'
  },
  dayTitle: {
    color: '#11324d',
    fontSize: '15px',
    fontWeight: 700
  },
  dayStatus: {
    color: '#5d7690',
    fontSize: '12px',
    fontWeight: 600
  },
  metricRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
    gap: '8px'
  },
  metric: {
    padding: '10px 12px',
    borderRadius: '10px',
    backgroundColor: '#f6f9fc',
    border: '1px solid #e4edf5'
  },
  metricLabel: {
    color: '#6b8195',
    fontSize: '11px',
    marginBottom: '4px'
  },
  metricValue: {
    color: '#11324d',
    fontSize: '13px',
    fontWeight: 700
  },
  detailBlock: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px'
  },
  detailTitle: {
    color: '#35526d',
    fontSize: '12px',
    fontWeight: 700
  },
  blockerList: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '8px'
  },
  blockerChip: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '6px 10px',
    borderRadius: '999px',
    backgroundColor: '#fff5e5',
    color: '#8a5a00',
    fontSize: '12px',
    fontWeight: 600
  },
  summaryList: {
    margin: 0,
    paddingLeft: '18px',
    color: '#35526d',
    fontSize: '13px',
    lineHeight: 1.7
  },
  summaryItem: {
    marginBottom: '4px'
  },
  emptyState: {
    color: '#5d7690',
    fontSize: '13px',
    lineHeight: 1.6
  }
};

export default CalendarContextSummaryMessage;
